import React from 'react'
import { Dashboard } from './components/dashboard'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { SignIn } from './components/signin'
import { AuthContextProvider, LoggedRoute, ProtectRoutes } from './context/AuthContext'
import { Project } from './components/project'
import { ProjectContextProvider } from './context/ProjectContext'
import { DashboardContextProvider } from './context/DashboardContext'
import { EventsContextProvider } from './context/EventsContext'
import { MeetingsContextProvider } from './context/MeetingContext'
import { RemoteConfigProvider } from './context/RemoteConfigContext'
import { WorkspaceProvider } from './context/WorkspaceContext'
import { Callback } from './components/signin/callback'
import { Toaster } from 'react-hot-toast'
import { CreateWorkspace } from './components/workspace/create'
import { SelectWorkspace } from './components/workspace/select'
import { Meeting } from './components/meeting'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ThemeProvider } from './context/ThemeContext'
import RecordingPage from './components/recording/RecordingPage'
import { RoadmapContextProvider } from './context/RoadmapContext'
import { FeedContextProvider } from './context/FeedContext'
import { initializeFirebase } from './firebase'
import { MobileApp } from './MobileApp'
import { useIsMobile } from './hooks/useIsMobile'

initializeFirebase()

// Check if current route should use mobile version
function useShouldUseMobileApp() {
  const location = useLocation()
  const mobileRoutes = ['/signin', '/callback', '/workspace', '/dashboard']

  return mobileRoutes.some((route) => location.pathname === route || location.pathname.startsWith(route + '/'))
}

// Main routing component that handles mobile/desktop logic
function AppRouter() {
  const isMobile = useIsMobile()
  const shouldUseMobileApp = useShouldUseMobileApp()

  // Use mobile app only for sign-in routes on mobile devices
  const useMobileApp = isMobile && shouldUseMobileApp

  return (
    <>
      {/* Conditionally render mobile or desktop app based on device detection and route */}
      {useMobileApp ? (
        <MobileApp />
      ) : (
        <Routes>
          <Route path="/callback" element={<Callback />} />
          <Route
            path="/signin"
            element={
              <LoggedRoute>
                <SignIn />
              </LoggedRoute>
            }
          />
          <Route
            path="/record"
            element={
              <ProtectRoutes>
                <RecordingPage />
              </ProtectRoutes>
            }
          />
          <Route
            path="/project/:id/record"
            element={
              <ProtectRoutes>
                <WorkspaceProvider>
                  <DashboardContextProvider>
                    <RecordingPage />
                  </DashboardContextProvider>
                </WorkspaceProvider>
              </ProtectRoutes>
            }
          />
          <Route
            path="/workspace"
            element={
              <ProtectRoutes>
                <Outlet />
              </ProtectRoutes>
            }
          >
            <Route path="" element={<Navigate to="create" />} />
            <Route path="create" element={<CreateWorkspace />} />
            <Route
              path="select"
              element={
                <DashboardContextProvider>
                  <SelectWorkspace />
                </DashboardContextProvider>
              }
            />
          </Route>
          <Route path="/dashboard" element={<Navigate to="/dashboard/home-new" />} />
          <Route
            path="/dashboard/:selectedMenu/:roadmapMenu?"
            element={
              <ProtectRoutes>
                <WorkspaceProvider>
                  <DashboardContextProvider>
                    <RoadmapContextProvider>
                      <FeedContextProvider>
                        <Dashboard />
                      </FeedContextProvider>
                    </RoadmapContextProvider>
                  </DashboardContextProvider>
                </WorkspaceProvider>
              </ProtectRoutes>
            }
          />
          <Route
            path="/dashboard/meetings/:meetingId"
            element={
              <ProtectRoutes>
                <WorkspaceProvider>
                  <DashboardContextProvider>
                    <MeetingsContextProvider>
                      <Meeting />
                    </MeetingsContextProvider>
                  </DashboardContextProvider>
                </WorkspaceProvider>
              </ProtectRoutes>
            }
          />
          <Route
            path="/project/:id"
            element={
              <ProtectRoutes>
                <WorkspaceProvider>
                  <DashboardContextProvider>
                    <ProjectContextProvider>
                      <Project />
                    </ProjectContextProvider>
                  </DashboardContextProvider>
                </WorkspaceProvider>
              </ProtectRoutes>
            }
          />
          {/* ASk anything route commented out while we need sharing or stable urls, right now it is likely buggy */}
          {/* <Route
                      path="/ask-anything/:threadId?"
                      element={
                        <ProtectRoutes>
                          <AskAnything />
                        </ProtectRoutes>
                      }
                    /> */}
          <Route path="*" element={<Navigate to="/signin" />} />
        </Routes>
      )}
    </>
  )
}

function App() {
  return (
    <main className="m-0 h-screen">
      <Toaster />
      <TooltipProvider>
        <BrowserRouter>
          <ThemeProvider>
            <RemoteConfigProvider>
              <AuthContextProvider>
                <EventsContextProvider>
                  <AppRouter />
                </EventsContextProvider>
              </AuthContextProvider>
            </RemoteConfigProvider>
          </ThemeProvider>
        </BrowserRouter>
      </TooltipProvider>
    </main>
  )
}

export default App
